import React, { createContext, useContext, useState, useEffect } from 'react';
import { createContextualCan } from '@casl/react';
import { defineAbilityFor } from './ability';
import { useAuth } from '../context/AuthContext';
import { useAuthPermissionsRetrieveQuery } from '../services/GLOWClient';

// Create the ability context
const AbilityContext = createContext();

// Create a Can component that will be used to check permissions
export const Can = createContextualCan(AbilityContext.Consumer);

// Custom hook to use the ability context
export const useAbility = () => {
  return useContext(AbilityContext);
};

// Provider component for the ability context
export const AbilityProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [ability, setAbility] = useState(defineAbilityFor(null));
  
  // Fetch permissions from the backend when the user is authenticated
  const { data: permissionsData, isLoading } = useAuthPermissionsRetrieveQuery(undefined, {
    skip: !isAuthenticated(),
  });
  
  useEffect(() => {
    if (!user) {
      // Reset ability when user logs out
      setAbility(defineAbilityFor(null));
      return;
    }

    // Build ability as soon as we have a user.
    // For superadmin, defineAbilityFor() grants full access without needing permissionsData.
    const permissions = permissionsData?.permissions || [];
    const newAbility = defineAbilityFor(user, permissions);
    setAbility(newAbility);
  }, [user, permissionsData]);
  
  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};
